import { message, Table } from 'antd'
import React, { useState } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'

import { CategoryFormModal } from './CategoryFormModal'
import { ICategory } from '../../../types/category'
import { axiosInstance } from '../../../hooks/useAxios'
import { IFilterMapped } from './CategoryFilter'
import CircularLoading from '../../../components/CircularProgress'
import EditIcon from '@mui/icons-material/Edit'
import dayjs from 'dayjs'
import { ONLY_DATE_FORMAT, ONLY_TIME_FORMAT } from '../../../constants/global'
import Pagination from '../../../components/Pagination'
import ConfirmModal from '../../../components/ConfirmModal'
import { ERRORS_MESSAGE_MAPPER } from '../../../constants/errors'
import { Tag } from '../../../components/Tag'

interface ICategoriesTableProps {
  isLoading: boolean
  data: ICategory[]
  onUpdateCategorySuccess: () => void
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  handlePageChange: (filter: IFilterMapped) => void
  total: number
}

const CategoriesTable = ({
  isLoading,
  data,
  onUpdateCategorySuccess,
  filter,
  onFilterChange,
  handlePageChange,
  total,
}: ICategoriesTableProps) => {
  const [categoryEdit, setCategoryEdit] = useState<ICategory>()
  const [isModalEditCategory, setIsModalEditCategory] = useState<boolean>(false)
  const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false)
  const [isModalDeleteCategory, setIsModalDeleteCategory] =
    useState<boolean>(false)
  const onHandleDeleteCategory = async (objectId: string) => {
    setIsLoadingDelete(true)
    try {
      await axiosInstance.delete(`/category/${objectId}`)
      message.success('ลบข้อมูลสำเร็จ')
      onUpdateCategorySuccess()
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
    setIsLoadingDelete(false)
  }

  const columns = [
    {
      title: 'ลำดับ',
      dataIndex: 'numberRow',
      key: 'numberRow',
      align: 'center' as const,
      width: 80,
      render: (numberRow: number) => {
        return (
          <Box>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              textAlign={'center'}
            >
              {numberRow}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'ชื่อหมวดหมู่',
      dataIndex: 'name',
      key: 'name',
      align: 'left' as const,
      render: (name: string, category: ICategory) => {
        return (
          <Typography variant="h6" color="text.secondary">
            {name}
          </Typography>
        )
      },
    },
    {
      title: 'รายละเอียด',
      dataIndex: 'description',
      key: 'description',
      align: 'center' as const,
      render: (description: string) => {
        return (
          <Typography variant="subtitle2" color="text.secondary">
            {description?.length > 0 ? description : <em>ไม่ระบุ</em>}
          </Typography>
        )
      },
    },
    {
      title: 'สถานะ',
      dataIndex: 'isAvailable',
      key: 'isAvailable',
      align: 'center' as const,
      render: (isAvailable: boolean) => {
        return (
          <Tag color={isAvailable ? 'success' : 'error'} style={{ margin: 0 }}>
            {isAvailable ? 'เปิดใช้งาน' : 'ระงับการใช้งาน'}
          </Tag>
        )
      },
    },
    {
      title: 'วันที่เพิ่ม',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      align: 'center' as const,
      render: (createdAt: string) => {
        return (
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {dayjs(createdAt).format(ONLY_DATE_FORMAT)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayjs(createdAt).format(ONLY_TIME_FORMAT)}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'วันที่แก้ไข',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
      align: 'center' as const,
      render: (updatedAt: string) => {
        return (
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {dayjs(updatedAt).format(ONLY_DATE_FORMAT)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayjs(updatedAt).format(ONLY_TIME_FORMAT)}
            </Typography>
          </Box>
        )
      },
    },
    {
      dataIndex: '',
      key: 'action',
      width: 120,
      align: 'center' as const,
      render: (_: undefined, category: ICategory) => {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <IconButton
              onClick={() => {
                setCategoryEdit(category)
                setIsModalEditCategory(true)
              }}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              onClick={() => {
                setCategoryEdit(category)
                setIsModalDeleteCategory(true)
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        )
      },
    },
  ]

  return (
    <>
      <ConfirmModal
        open={isModalDeleteCategory}
        title={'ลบหมวดหมู่'}
        message={`คุณต้องการลบหมวดหมู่ ${categoryEdit?.name} ใช่หรือไม่?`}
        onClose={() => setIsModalDeleteCategory(false)}
        onConfirm={() => {
          onHandleDeleteCategory(categoryEdit?.objectId!)
          setIsModalDeleteCategory(false)
        }}
      />
      <CategoryFormModal
        isOpen={isModalEditCategory}
        onUpdateCategorySuccess={onUpdateCategorySuccess}
        onClose={(visible) => setIsModalEditCategory(visible)}
        category={categoryEdit}
      />
      <CircularLoading isLoading={isLoading || isLoadingDelete} />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) =>
          handlePageChange({ ...filter, page, perPage })
        }
        mb={2}
      />
      <Table
        size={'middle'}
        columns={columns}
        scroll={{
          x: columns.reduce(
            (pre, cur) => (cur.width ? cur.width + pre : pre),
            0,
          ),
        }}
        dataSource={data?.map((category: ICategory, index: number) => {
          return {
            ...category,
            key: category.objectId,
            numberRow:
              index + 1 + ((filter.page || 0) - 1) * (filter.perPage || 0),
          }
        })}
        bordered
        pagination={false}
      />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) =>
          handlePageChange({ ...filter, page, perPage })
        }
      />
    </>
  )
}

export default CategoriesTable
